"use client";

import React from "react";
import { MessageSquare, Camera, Sparkles, Heart, ArrowRight, Image } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  id: 1,
  title: "The Consultation",
  description: "A quiet moment to share your vision, discuss the timeline, and align our artistic styles for your day.",
  icon: MessageSquare
}, {
  id: 2,
  title: "The Session",
  description: "An intimate engagement shoot designed to build comfort before the camera and capture your natural chemistry.",
  icon: Camera
}, {
  id: 3,
  title: "The Big Day",
  description: "Seamless, unobtrusive coverage of every fleeting emotion, from the quiet morning prep to the final dance.",
  icon: Sparkles
}, {
  id: 4,
  title: "The Curation",
  description: "Meticulous selection and soft-toned editing of your gallery to ensure a cohesive, timeless visual narrative.",
  icon: Image
}, {
  id: 5,
  title: "The Heirloom",
  description: "The delivery of your bespoke fine-art album, crafted to preserve your legacy for generations to come.",
  icon: Heart
}];
export default function CustomDesignProcess() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224638" id="the-ivory-journey" className="relative w-full bg-background py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
      backgroundImage: 'linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px)',
      backgroundSize: '4rem 4rem'
    }} aria-hidden="true" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block py-1 px-3 mb-4 text-xs font-mono font-semibold tracking-widest text-primary border border-border rounded-lg bg-secondary uppercase">
            The Experience
          </span>
          <h2 className="text-4xl md:text-6xl font-light font-default text-foreground mb-6 tracking-tight">
            The Ivory Journey
          </h2>
          <p className="text-lg text-secondary-foreground font-sans font-light leading-relaxed">
            A refined, five-step approach to capturing your wedding story with the grace, sophistication, and timelessness it deserves.
          </p>
        </div>

        <div ref={timelineRef} className={`transition-all duration-1000 delay-200 ease-out ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <ScrollArea className="w-full pb-8 -mx-4 px-4 md:mx-0 md:px-0">
            <div className="flex min-w-max relative pt-12 pb-4 px-4 md:px-8">
              {}
              <div className="absolute top-[4.5rem] left-[9rem] right-[9rem] h-px border-t border-dashed border-ring z-0" aria-hidden="true" />

              {steps.map((step, i) => <div key={step.id} data-index={i} className="relative flex flex-col items-center w-72 shrink-0 mx-4 group">
                  {}
                  <div className="z-10 flex flex-col items-center mb-6">
                    <div className="text-4xl font-default text-foreground/10 mb-3 font-light italic tracking-tighter">
                      0{step.id}
                    </div>
                    <div className="flex items-center justify-center w-16 h-16 rounded-lg bg-background border border-border group-hover:border-ring group-hover:text-primary text-muted-foreground shadow-sm transition-all duration-500 relative">
                      {}
                      <div className="absolute inset-0 rounded-lg bg-accent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <step.icon className="w-5 h-5 relative z-10 stroke-[1.25]" />
                    </div>
                  </div>

                  {}
                  <Card className="w-full h-full border-none shadow-xs bg-card/40 backdrop-blur-md transition-all duration-500 group-hover:shadow-lg group-hover:bg-card/80">
                    <CardHeader className="pb-3 text-center">
                      <CardTitle className="font-default font-medium text-xl text-card-foreground tracking-tight">
                        {step.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-center">
                      <p className="text-sm text-secondary-foreground font-sans font-light leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
            <ScrollBar orientation="horizontal" className="h-1.5" />
          </ScrollArea>
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" className="font-sans font-light tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-all duration-300">
            <Link to="/InquiryPage">
              Begin Your Story
              <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}